import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { ActionTargetType, ChecklistStap } from '@fleet/shared';
import { Button } from '../../design-system';
import { Panel } from '../../components/ui/Panel';
import { SectieKop } from '../../components/ui/SectieKop';
import { openChecklistMail } from '../car-detail/checklistMail';
import { ActionCenter } from './ActionCenter';
import { BuitenlandPanel } from './BuitenlandPanel';
import { DuursteAutos } from './DuursteAutos';
import { FleetRings } from './FleetRings';
import { FleetTcoPanel } from './FleetTcoPanel';
import { KostenPerPersoon } from './KostenPerPersoon';
import { MonthlyCostStack } from './MonthlyCostStack';
import { PeriodeKiezer } from '../../components/ui/PeriodeKiezer';
import { TankGedragPanel } from './TankGedragPanel';
import { TrackerRow } from './TrackerRow';
import { WasBudgetPanel } from './WasBudgetPanel';
import { useChecklistStap } from '../fleet/fleet.api';
import { buitenlandXlsxUrl, useBuitenlandOordeel, useDashboard } from './dashboard.api';

export function DashboardPage() {
  // Twee losse periodes: "welke auto kostte in maart het meest" en "wie tankte
  // dit jaar het meest" zijn verschillende vragen en horen niet aan elkaar vast
  // te zitten. React Query cachet op de periode, dus zolang beide gelijk staan is
  // het één request; pas als je ze uit elkaar trekt komt er een tweede bij.
  const [periodTco, setPeriodTco] = useState<string | null>(null);
  const [periodPersoon, setPeriodPersoon] = useState<string | null>(null);
  // En "wie tankte duur" is er nog een derde van: die vraag stel je over een jaar
  // om een gewoonte te zien, of juist over één maand om iemand aan te spreken op
  // wat er net gebeurd is.
  const [periodTank, setPeriodTank] = useState<string | null>(null);
  const { data, isLoading, isError } = useDashboard(periodTco);
  const persoonQuery = useDashboard(periodPersoon);
  const tankQuery = useDashboard(periodTank);
  const checklistStap = useChecklistStap();
  const buitenlandOordeel = useBuitenlandOordeel();
  const navigate = useNavigate();

  function onNavigate(target: ActionTargetType, id: string) {
    if (target === 'car') navigate(`/auto/${encodeURIComponent(id)}`);
    else if (target === 'pas') navigate(`/passen/${encodeURIComponent(id)}`);
    else navigate(`/personen/${encodeURIComponent(id)}`);
  }

  /** Waar een checklist-stap gedaan wordt. */
  function onChecklistStap(stap: ChecklistStap, kenteken: string) {
    const doel = stap.doel;
    // Naar de autopagina, waar dezelfde checklist staat: daar wijst dezelfde stap
    // door naar de kaart met het veld erin.
    if (doel.soort === 'auto') navigate(`/auto/${encodeURIComponent(kenteken)}`);
    else if (doel.soort === 'passen') navigate('/passen');
    else if (doel.soort === 'personen') navigate('/personen');
    else if (doel.soort === 'mailto') openChecklistMail(doel);
  }

  function onChecklistToggle(kenteken: string, stap: ChecklistStap) {
    checklistStap.mutate({ kenteken, stap: stap.key, gedaan: !stap.gedaan });
  }

  if (isError) return <div style={{ color: '#8A4029' }}>Dashboard kon niet worden geladen.</div>;
  if (isLoading || !data) return <div style={{ color: '#888888' }}>Dashboard laden…</div>;

  // Valt de tweede vraag nog te laden, dan leunt hij zolang op de eerste; bij
  // gelijke periode is het sowieso dezelfde respons uit de cache.
  const persoonData = persoonQuery.data ?? data;
  const tankData = tankQuery.data ?? data;
  const hasCosts = persoonData.costPerPersoon.length > 0;

  // De vier trackers verdelen over de twee secties: TCO, vaste lasten en
  // restwaarde gaan over het geheel, de variabele laag hoort bij de sectie
  // eronder — samen met de prijzen per liter, kWh, parkeeractie en wasbeurt.
  const totaalTegels = data.trackers.filter((t) => t.key !== 'variabel');
  const variabelTegels = data.trackers.filter((t) => t.key === 'variabel');

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {/* Waaruit het wagenpark bestaat gaat voorop: dat is de noemer waar alle
          bedragen eronder tegen gelezen worden, inclusief de per-auto-schakelaar. */}
      <SectieKop titel="Wagenpark" />
      <FleetRings
        fleet={data.ringFleet}
        drivetrain={data.ringDrivetrain}
        eigendom={data.ringEigendom}
        donut={data.donut}
      />

      <SectieKop titel="Actiecentrum" />
      <ActionCenter
        groups={data.actionCenter}
        dataGaten={data.dataGaten}
        autoChecklists={data.autoChecklists}
        buitenlandTwijfel={data.buitenland.twijfel}
        onNavigate={onNavigate}
        onChecklistStap={onChecklistStap}
        onChecklistToggle={onChecklistToggle}
        onBuitenlandOordeel={(costLineIds, oordeel) =>
          buitenlandOordeel.mutate({ costLineIds, oordeel })
        }
        buitenlandBezig={buitenlandOordeel.isPending}
      />

      {/* Per sectie eerst de kengetallen, dan de grafieken die ze uitwerken. Zo
          staat elk cijfer boven het beeld waar het uit komt, in plaats van acht
          tegels op een hoop bovenaan de pagina. */}
      <SectieKop titel="Totale kosten wagenpark" />
      <TrackerRow trackers={totaalTegels} laatsteTransactie={data.laatsteTransactie} />
      {data.vlootTco && <FleetTcoPanel tco={data.vlootTco} />}
      <DuursteAutos
        rijen={data.tcoPerAuto}
        periodLabel={data.periodLabel}
        months={data.months}
        period={data.period}
        onPeriod={setPeriodTco}
        onSelectCar={(k) => navigate(`/auto/${encodeURIComponent(k)}`)}
      />

      <SectieKop titel="Variabele kosten wagenpark" />
      <TrackerRow trackers={[...variabelTegels, ...data.prijsTrackers]} />
      <MonthlyCostStack months={data.monthlyStack} />
      {hasCosts ? (
        <KostenPerPersoon
          rijen={persoonData.costPerPersoon}
          periodLabel={persoonData.periodLabel}
          months={persoonData.months}
          period={persoonData.period}
          onPeriod={setPeriodPersoon}
          onSelectPerson={(id) => navigate(`/personen/${encodeURIComponent(id)}`)}
        />
      ) : (
        <Panel style={{ border: '1px dashed #C9BBA8', padding: 40, textAlign: 'center' }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: '#111111' }}>
            Nog geen kostendata.
          </div>
          <p style={{ margin: '6px 0 16px', fontSize: 14, color: '#666666' }}>
            Upload MTC-tankpasfacturen en onderhoudsfacturen om het kostenoverzicht te vullen.
          </p>
          <Button variant="primary" onClick={() => navigate('/facturen')}>
            Facturen uploaden
          </Button>
        </Panel>
      )}

      {/* De periodekiezer staat hier in de sectiekop en niet in een van de twee
          kaarten: hij stuurt ze allebei, en in één kaart zou hij lijken te gaan
          over alleen die ene. */}
      <SectieKop titel="Tanken en laden">
        <PeriodeKiezer months={tankData.months} period={tankData.period} onPeriod={setPeriodTank} />
      </SectieKop>
      <TankGedragPanel gedrag={tankData.tankGedrag} periodLabel={tankData.periodLabel} />
      {/* Eigen periodekiezer en niet de chip in de kop hierboven, en dat blijft zo nu
          er ook hier Alle/Dit jaar staat. Twee redenen. Deze kaart begint op de
          nieuwste maand mét buitenlandtransacties en niet simpelweg op de nieuwste,
          anders open je op een lege maand terwijl er verderop wél iets is. En hij
          filtert wat hij al binnen heeft: alle maanden zitten in `data.buitenland`,
          dus wisselen kost geen nieuwe dashboardrespons, waar de chip hierboven er
          wel een ophaalt. Die keuze bepaalt ook wat er in de Excel komt. */}
      <BuitenlandPanel overzicht={data.buitenland} exportUrl={buitenlandXlsxUrl} />

      <SectieKop titel="Wasbudget" />
      <WasBudgetPanel was={data.wasBudget} />
    </div>
  );
}
